import { useState } from 'react';
import { Mail, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { useRequestLogin } from '../../hooks/useAuth';
import { ApiError } from '../../lib/api';

export function LoginView() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const loginMutation = useRequestLogin();

  // Extract error details
  const errorBody = loginMutation.error instanceof ApiError
    ? (loginMutation.error.body as { error?: string; message?: string } | undefined)
    : undefined;
  const isNoAccount = errorBody?.error === 'NO_ACCOUNT';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    loginMutation.mutate(email.trim(), {
      onSuccess: () => setSent(true),
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-brand-950">
      <div className="w-full max-w-md px-4">
        <div className="card p-8">
          {/* Logo */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600">
              <span className="text-2xl font-bold text-white">Z</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Zachary CRM</h1>
            <p className="mt-1 text-sm text-slate-500">Sign in to your account</p>
          </div>

          {sent ? (
            /* Success state */
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Check your email</h2>
              <p className="mt-2 text-sm text-slate-500">
                We sent a sign-in link to <strong className="text-slate-700">{email}</strong>.
                Click the link in the email to sign in.
              </p>
              <p className="mt-2 text-xs text-slate-400">
                The link expires in 15 minutes and can only be used once.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setEmail('');
                  loginMutation.reset();
                }}
                className="btn-ghost mt-6 text-brand-600"
              >
                Use a different email
              </button>
            </div>
          ) : (
            /* Login form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="label mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      // Clear NO_ACCOUNT error when user changes email
                      if (loginMutation.error) loginMutation.reset();
                    }}
                    placeholder="you@company.com"
                    className="input pl-10"
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>
              </div>

              {isNoAccount && (
                <div className="flex gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">No account found</p>
                    <p className="mt-0.5 text-amber-700">
                      There's no CRM account for this email address. Contact your administrator to get access.
                    </p>
                  </div>
                </div>
              )}

              {loginMutation.error && !isNoAccount && (
                <div className="rounded-md bg-danger-light px-3 py-2 text-sm text-red-700">
                  Failed to send sign-in link. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={loginMutation.isPending || !email.trim()}
                className="btn-primary w-full"
              >
                {loginMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Checking...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Send Sign-In Link
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Zachary Companies &mdash; Insurance Technology Platform
        </p>
      </div>
    </div>
  );
}
